import { useMemo } from "react";

import CloudinaryImage from "@/components/cloudinary-image";
import { cn } from "@/lib/utils";
import { type GameImageType, OverallRatingEnum } from "@/pages/search/types";

import {
  CompletedIcon,
  DislikeIcon,
  LikeIcon,
  LoveIcon,
  SavedIcon,
} from "./icons";
import StatusBox from "./status-box";
import UserActions, {
  type UserActionsProps,
  type UserGameType,
} from "./user-actions";

interface ImageGameProps extends UserGameType {
  gameimageSet?: GameImageType[];
}

interface GameListImageProps extends UserActionsProps {
  children?: React.ReactNode;
  className?: string;
  game: ImageGameProps;
  iconBoxClassName?: string;
  withoutActions?: boolean;
}

export default function GameListImage({
  children,
  className,
  game,
  iconBoxClassName,
  onGameCompleted,
  withoutActions = false,
}: GameListImageProps) {
  const RatingIcon = useMemo(() => {
    if (game.overallRating === OverallRatingEnum.DISLIKE) {
      return DislikeIcon;
    }
    if (game.overallRating === OverallRatingEnum.LIKE) {
      return LikeIcon;
    }
    if (game.overallRating === OverallRatingEnum.LOVE) {
      return LoveIcon;
    }
    return null;
  }, [game.overallRating]);

  return (
    <div
      className={cn(
        "relative size-10 shrink-0 shadow-0-0-10 shadow-black/44 md:size-32.5",
        className
      )}
    >
      <CloudinaryImage
        className="size-full rounded-[5px] transition-opacity md:rounded-[3px] group-hover:md:opacity-30"
        game={game}
        rW={260}
      />
      {!RatingIcon && children}
      {!!game.isCompleted && (
        <div className="absolute inset-0 flex items-center justify-center rounded-[5px] bg-linear-227 from-mint-turquoise/40 to-bright-turquoise/40 md:rounded-[3px]">
          <CompletedIcon />
        </div>
      )}
      {!!game.isSaved && !game.isCompleted && (
        <StatusBox
          className={cn(
            "bg-linear-180 from-rose-red to-plum",
            iconBoxClassName
          )}
        >
          <SavedIcon className="aspect-[5/9] h-auto w-1/3" />
        </StatusBox>
      )}
      {!!RatingIcon && (
        <StatusBox className={iconBoxClassName} rating={game.overallRating}>
          <RatingIcon className="aspect-square h-auto w-[60%]" />
        </StatusBox>
      )}
      {!withoutActions && (
        <UserActions game={game} onGameCompleted={onGameCompleted} />
      )}
    </div>
  );
}
